/**
 * Themes Settings Page
 *
 * Full-page theme browser with search, color previews,
 * and a toggle for adapting the logo to the active theme.
 */

import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MagnifyingGlass, Check } from "@phosphor-icons/react";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { THEMES } from "../../../lib/themes";
import type { ThemeDefinition } from "../../../lib/themes";
import { applyTheme } from "../../../lib/theme-engine";
import { useThemeStore } from "../../../stores/themeStore";

export const Route = createFileRoute("/_authenticated/settings/themes")({
  component: ThemesPage,
});

function ThemesPage() {
  const [search, setSearch] = useState("");
  const { currentThemeId, setThemeId } = useThemeStore();
  const themedLogo = useThemeStore((s) => s.themedLogo);
  const setThemedLogo = useThemeStore((s) => s.setThemedLogo);
  const duotoneIcons = useThemeStore((s) => s.duotoneIcons);
  const setDuotoneIcons = useThemeStore((s) => s.setDuotoneIcons);

  const filtered = useMemo(
    () =>
      THEMES.filter((t) =>
        t.name.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  function handleSelect(theme: ThemeDefinition) {
    applyTheme(theme);
    setThemeId(theme.id);
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Themes" }]} />

      {/* Search + Logo toggle */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass
            size={16}
           
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search themes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border-[0.5px] border-input rounded-lg outline-none focus:ring-1 focus:ring-ring"
            data-testid="theme-search-input"
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <span className="text-muted-foreground">Adapt logo to theme</span>
          <div
            style={{ position: "relative", display: "inline-block", width: 36, height: 20 }}
            data-testid="themed-logo-toggle"
          >
            <input
              type="checkbox"
              checked={themedLogo}
              onChange={(e) => setThemedLogo(e.target.checked)}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 9999,
                backgroundColor: themedLogo ? "var(--color-primary)" : "#d1d5db",
                transition: "background-color 0.2s",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 2,
                left: themedLogo ? 18 : 2,
                width: 16,
                height: 16,
                borderRadius: 9999,
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </div>
        </label>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <span className="text-muted-foreground">Duotone icons</span>
          <div
            style={{ position: "relative", display: "inline-block", width: 36, height: 20 }}
            data-testid="duotone-icons-toggle"
          >
            <input
              type="checkbox"
              checked={duotoneIcons}
              onChange={(e) => setDuotoneIcons(e.target.checked)}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 9999,
                backgroundColor: duotoneIcons ? "var(--color-primary)" : "#d1d5db",
                transition: "background-color 0.2s",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 2,
                left: duotoneIcons ? 18 : 2,
                width: 16,
                height: 16,
                borderRadius: 9999,
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </div>
        </label>
      </div>

      {/* Theme grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map((theme) => {
          const isActive = currentThemeId === theme.id;
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => handleSelect(theme)}
              className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-[0.5px] transition-colors cursor-pointer ${
                isActive
                  ? "border-primary bg-accent"
                  : "border-input hover:border-primary/50 hover:bg-accent/30"
              }`}
              data-testid={`theme-option-${theme.id}`}
            >
              {isActive && (
                <Check
                  size={14}
                  weight="bold"
                  className="absolute top-2 right-2 text-primary"
                />
              )}
              {/* Color preview bubbles */}
              <div
                className="flex gap-1 rounded-full px-2 py-1.5"
                style={{
                  backgroundColor: theme.colors.bg,
                  outline: `2px solid ${theme.colors.bg}`,
                }}
              >
                <span
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: theme.colors.main }}
                />
                <span
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: theme.colors.text }}
                />
                <span
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: theme.colors.sub }}
                />
              </div>
              <span className="text-sm font-medium truncate w-full text-center">
                {theme.name}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No themes found
        </div>
      )}
    </div>
  );
}
